<template>
  <tr
    class="ce-productSheet">
    <td class="ce-productSheet-actions">
      <div class="flex items-center">
        <span class="ce-productSheet-typeIconWrapper">
          <fa-icon
            @click="startDownload"
            :icon="['far', 'file-pdf']" />
        </span>

        <a
          class="ce-productSheet-favoriteCta"
          href=""
          @click.prevent="toggleFavorite">
          <fa-icon :icon="favoriteStateIcon" />
        </a>
      </div>
    </td>
    <td
      class="ce-productSheet-label"
      @click="startDownload">
      {{ asset.label }}

      <fa-icon
        v-if="mostRestrictiveAccessLevel && mostRestrictiveAccessLevel.code === 'internal'"
        transform="up-6 shrink-4"
        icon="lock" />
    </td>

    <MsdsDocumentDownloadModal
      v-if="downloadModalVisible"
      @close="cancelDownload"
      :emg="product.prd" />
  </tr>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import MsdsDocumentDownloadModal from 'components/MsdsDocumentDownloadModal.vue';
import { cloneObject, sortArray } from 'utils';

export default {
  props: {
    asset: {
      type: Object,
      required: true
    },
    product: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      downloadModalVisible: false,
    };
  },
  components: { MsdsDocumentDownloadModal },
  computed: {
    ...mapState(['documentFavorite', 'fieldManifest']),
    isFavored() {
      if (this.documentFavorite) {
        // No document types defined, so the whole pigment is favored
        if (!this.documentFavorite.documentTypes) return true;

        if (Array.isArray(this.documentFavorite.documentTypes)) {
          // No document types defined, so the whole pigment is favored
          if (this.documentFavorite.documentTypes.length === 0) return true;

          // Limited to some document types, check if the current asset type is included
          return this.documentFavorite.documentTypes.indexOf(this.asset.type) > -1;
        }
      }

      return false;
    },
    favoriteStateIcon() {
      return this.isFavored ? ['fas', 'star'] : ['far', 'star'];
    },
    manifestForAssetType() {
      return this.fieldManifest.hasOwnProperty(this.asset.type)
        ? this.fieldManifest[this.asset.type]
        : null;
    },
    accessLevelsForAsset() {
      if (this.manifestForAssetType) {
        const accessLevels = cloneObject(this.manifestForAssetType.accessLevel);

        return accessLevels.filter((acl) => this.asset.industries.includes(acl.industry));
      }

      return null;
    },
    mostRestrictiveAccessLevel() {
      const accessLevels = cloneObject(this.accessLevelsForAsset);

      if (accessLevels) {
        sortArray(accessLevels, 'weight', 'asc');

        return accessLevels.shift();
      }

      return null;
    }
  },
  methods: {
    ...mapActions(['saveDocumentFavorite', 'deleteDocumentFavorite']),
    startDownload() {
      this.downloadModalVisible = true;
    },
    cancelDownload() {
      this.downloadModalVisible = false;
    },
    toggleFavorite() {
      if (this.isFavored) this.removeFavorite();
      else this.addFavorite();
    },
    addFavorite() {
      const payload = {
        productId: this.product.prd,
        documentTypes: [this.asset.type]
      };

      // Merge already favored asset types
      if (this.documentFavorite && this.documentFavorite.documentTypes && Array.isArray(this.documentFavorite.documentTypes)) {
        payload.documentTypes = payload.documentTypes.concat(this.documentFavorite.documentTypes);
      }

      this.saveDocumentFavorite(payload);
    },
    removeFavorite() {
      let favoredTypes;

      if (this.documentFavorite
        && this.documentFavorite.documentTypes
        && Array.isArray(this.documentFavorite.documentTypes)
        && this.documentFavorite.documentTypes.length > 0) {
        // Is favorite for x document types, remove the current one from the array
        favoredTypes = this.documentFavorite.documentTypes.filter((type) => type !== this.asset.type);
      } else {
        // Is favorite on pigment level, get all visible asset types except the current one
        favoredTypes = this.product.assets.map((a) => a.type).filter((type) => type !== this.asset.type);
      }

      // Delete the favorite if no types are left, update otherwise
      if (favoredTypes.length === 0) {
        this.deleteDocumentFavorite(this.documentFavorite.id);
      } else {
        this.saveDocumentFavorite({
          productId: this.product.prd,
          documentTypes: favoredTypes
        });
      }
    },
  }
};
</script>
